/**
 * @file: useReservationListControls.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * Single owner of every reservation-list control: the status tab, the debounced
 * search keyword, and the sort order. Combines them into the `filter` / `sorter`
 * the list + stats queries consume, and exposes the display state the control
 * bar renders from.
 */

import {
  ReservationListFilter,
  ReservationSorter,
} from '@euler/model/reservation/types';
import { useBehaviorSubject, useCreation } from '@euler/utils/hooks';
import { useEffect, useMemo, useState } from 'react';
import { BehaviorSubject, debounceTime, distinctUntilChanged, map } from 'rxjs';
import {
  kReservationSortOptions,
  kReservationStatusTabs,
} from './reservationListConstants';

export const useReservationListControls = () => {
  const [statusKey, setStatusKey] = useState('all');
  // The raw search input drives an RxJS subject; the debounced + trimmed value
  // emitted from it becomes the `keyword` that queries consume.
  const searchText$ = useCreation(() => new BehaviorSubject<string>(''), []);
  const [searchText, setSearchText] = useBehaviorSubject(searchText$);
  const [keyword, setKeyword] = useState('');
  const [sortKey, setSortKey] = useState(kReservationSortOptions[0].key);

  // Debounce the raw input into the value that actually drives the queries, so
  // each keystroke doesn't fire a list + stats request. `distinctUntilChanged`
  // drops no-op edits (e.g. trailing whitespace that trims to the same text).
  useEffect(() => {
    const sub = searchText$
      .pipe(
        debounceTime(300),
        map(text => text.trim()),
        distinctUntilChanged(),
      )
      .subscribe(setKeyword);
    return () => sub.unsubscribe();
  }, [searchText$]);

  // The keyword runs a fuzzy backend search (OR-LIKE over license plate /
  // customer name / phone). Shared by the list filter and the stats filter so
  // both narrow to the same rows.
  const searchFilter = useMemo<ReservationListFilter>(
    () => (keyword ? { keyword } : {}),
    [keyword],
  );

  const filter = useMemo<ReservationListFilter | undefined>(() => {
    const tab = kReservationStatusTabs.find(t => t.key === statusKey);
    const next: ReservationListFilter = { ...searchFilter };
    if (tab?.status) next.status = tab.status;
    return Object.keys(next).length ? next : undefined;
  }, [statusKey, searchFilter]);

  const sorter = useMemo<ReservationSorter>(
    () =>
      (
        kReservationSortOptions.find(o => o.key === sortKey) ??
        kReservationSortOptions[0]
      ).sorter,
    [sortKey],
  );

  return {
    // Combined query inputs.
    filter,
    sorter,
    // The search-only slice of the filter, for the stats counts.
    searchFilter,
    // Status tab.
    statusKey,
    setStatusKey,
    // Search.
    searchText,
    setSearchText,
    // Sort.
    sortKey,
    setSortKey,
  };
};

export type ReservationListControls = ReturnType<
  typeof useReservationListControls
>;
